import { ReactElement, useContext, useEffect, useState } from "react";
import RowComponent from "./dashboard/rowComponent";
import Toolbar from "./dashboard/toolbar";
import { DashboardSchemaType, IAcquisitionCell, ICell, IContext, IDashboard, IDashboardResult } from "./types";
import { simpleFetch, useAsyncFetch, useFetch } from "../../system/useFetch";
import { IDictionary, ISelectedNode, IState, Modules } from "../../types";
import { useSelector } from "react-redux";
import { DateTime } from "luxon";
import { SystemContext } from "../../context/systemContext";
import { getDashboardID, isKioskMode } from "../../configuration/configuration";
import BrowserItems from "./dashboard/browserItems";

interface IProps {
  parentSize: {
    width: number;
    height: number;
  };
}

const getTemplateRows = (rows: number, normal: boolean) => {
  let template =  isKioskMode() ? "1fr" : "64px 1fr ";

  for (let i = 1; normal && i < rows; i++) {
    template += "1fr ";
  }

  return template;
};

const emptyContext: IContext = {
  dashboard: null,
  from: DateTime.now().minus({ hour: 1 }),
  to: DateTime.now(),
  factoryDay: DateTime.now()
};

interface IPaletteController {
  counter: number;
  palette: IDictionary<boolean>;
}

const delay = async  (ms: number): Promise<void> => new Promise(resolve => setTimeout(resolve, ms));

export const paletteController: IPaletteController = { counter: 0, palette: {} };

let timer: NodeJS.Timeout | null = null;
const resetTimer = () => {
  if(timer) {
    clearInterval(timer);
    timer = null;
  }
};

const InterruptionDashboard = ({ parentSize }: IProps): ReactElement => {
  const systemContext = useContext(SystemContext);
  const selectedNode = useSelector<IState, ISelectedNode>(state => state.selectedNode);
  const [ context, setContext ] = useState<IContext>(emptyContext);
  const [ selectedCell, setSelectedCell ] = useState<ICell | IAcquisitionCell | null>(null);
  const [ schemas, setSchemas ] = useState<Record<DashboardSchemaType, any> | null>(null);
  // const [ getDashboardData, { loading, data: responseData }] = useLazyFetch<IDictionary<any>>(true);
  // const [ getDashboardData ] = useAsyncFetch<IDashboardResult>();
  const [ loading, setLoading ] = useState<IDictionary<boolean>>({});
  const [ cellData, setCellData] = useState< IDashboardResult>({});
  const [ getDashboard ] = useAsyncFetch<IDashboard>();
  const marqueeTime = context.dashboard?.customConfig.marqueeTime ?? 0;
  const { data: dashboardData } = useFetch<[IDashboard[], Record<DashboardSchemaType, any> | null]>([
    {
      module: "dashboard",
      query: "getDashboards",
      payload: {
        nodeID: selectedNode.nodeID,
        nodeType: selectedNode.type,
      }
    },
    {
      module: "dashboard",
      query: "getSchemaConfig",
      payload: {
        nodeID: selectedNode.nodeID,
        nodeType: selectedNode.type
      }
    }
  ]);

  const onUpdate = (db: IDashboard | null = null) => {
    const cells: (ICell | IAcquisitionCell)[] = [];
    const newCellData:  IDashboardResult = db ? {} : { ...cellData };
    const newLoading: IDictionary<boolean> = {};

    (db ?? context.dashboard!).layout.rows.forEach(row => {
      row.forEach(cell => {
        cells.push(cell);
        newLoading[cell.key] = true;
      });
    });

    paletteController.counter = cells.length;
    paletteController.palette = {};

    Object.keys((db ?? context.dashboard!).layout.palette).forEach(key =>  {
      paletteController.palette[key] = false;
    });

    setLoading(newLoading);

    const processRequest = async () => {
      const dBoard = db ?? context.dashboard!;

      for (const cell of cells) {

        const request = await simpleFetch<IDashboardResult>({
          module: "dashboard",
          query: "getDashboardData",
          payload: {
            nodeID: selectedNode.nodeID,
            nodeType: selectedNode.type,
            moduleID: Modules.INTERRUPTIONS,
            factoryDay: !dBoard.isAcquisition ? context.factoryDay.toFormat("yyyy-MM-dd") : undefined,
            from: dBoard.isAcquisition ? context.from.toFormat("yyyy-MM-dd HH:mm") + ":00" : undefined,
            to: dBoard.isAcquisition ? context.to.toFormat("yyyy-MM-dd HH:mm") + ":00" : undefined,
            cells: [ cell ],
            disableMariaDB: systemContext.user?.permissions.find(x => x.code === "DEV") !== undefined
          }
        });

        if (request) {
          newCellData[cell.key] = request[cell.key];
          newLoading[cell.key] = false;

          setCellData({ ...newCellData });
          setLoading({ ...newLoading });
        }

        await delay(100);
      }
    };

    processRequest().then();
  };

  const onOpenDashboard = (item: IDashboard): void => {

    if (!context.dashboard || context.dashboard.dashboardID !== item.dashboardID) {
      const now = DateTime.now();

      setContext({
        ...context,
        dashboard: item,
        from: now.minus({ hour: item.customConfig.range }),
        to: now,
        factoryDay: now
      });

      if (!item.customConfig.autoUpdate.enabled) {
        onUpdate(item);
      }
    }
  };

  useEffect(() => {
    const dashboardID = getDashboardID();
    if (dashboardID) {
      getDashboard(    {
        module: "dashboard",
        query: "getDashboard",
        payload: {
          dashboardID
        }
      }).then(dashboard => {

        if(dashboard){
          onOpenDashboard(dashboard);
        }
      });
    }
  }, []);

  const updateDashboard = (dashboard: IDashboard) => {
    setContext({ ...context, dashboard });
  };

  const onUpdatePalette = (p: IDictionary<string>, hasChange = false) => {
    let changed = hasChange;
    paletteController.counter--;

    if(paletteController.counter === 0) {
      for (const [ k, v ] of Object.entries(paletteController.palette)) {
        if (!v) {
          delete p[k];
        }
      }

      changed = true;
    }

    if (changed){
      updateDashboard({ ...context.dashboard!, layout: { ...context.dashboard!.layout, palette: p } });
    }
  };

  useEffect(() => {
    if (!dashboardData || dashboardData.length < 2) {
      return;
    }

    if (dashboardData[1]) {
      setSchemas(dashboardData[1]);
    }

    if (dashboardData[0] && dashboardData[0].length > 0) {
      const item = dashboardData[0].find(d => d.isDefault && d.userID === systemContext.user?.userID);
      // if (!item) {
      //   item = dashboardData[0].find(d => d.isDefault);
      // }

      if (item) {
        onOpenDashboard(item);
      }
    }
  }, [dashboardData]);

  const onSetContext = (cxt: IContext, newDashboard: boolean) => {
    setContext(cxt);
    if (newDashboard) {
      setCellData({});
      setSelectedCell(null);
    }
  };

  useEffect(() => {
    if (marqueeTime > 0) {
      let index = 0;
      const newCells: ICell[] = [];
      context.dashboard!.layout.rows.forEach(cells => {
        newCells.push(...cells as ICell[]);
      });

      resetTimer();
      if (newCells.length > 1) {
        timer = setInterval(() => {
          index = (index + 1) % newCells.length;
          setSelectedCell(newCells[index]);
        }, marqueeTime * 1000);
      }
    }

    return () => {
      resetTimer();
      setSelectedCell(null);
    };
  }, [ context.dashboard ]);

  const layoutStyle = {
    gridTemplateColumns: "1fr",
    gridTemplateRows: context.dashboard ? getTemplateRows(context.dashboard!.layout.rows.length, selectedCell === null) : "1fr",
    gridRowGap: "1em",
    ...parentSize
  };

  const height = (parentSize.height - 64) / (!context.dashboard || selectedCell ? 1 : context.dashboard!.layout.rows.length);

  return (
    <div className="display-grid" style={layoutStyle}>
      {!context.dashboard && <BrowserItems setDashboard={onOpenDashboard} selectedNode={selectedNode} />  }

      {!isKioskMode() && context.dashboard &&
        <Toolbar dashboard={context.dashboard} context={context} setContext={onSetContext} onUpdate={onUpdate}
          onOpenDashboard={onOpenDashboard}/>
      }

      { !selectedCell && schemas && context.dashboard && context.dashboard.layout.rows.map((cells, index) => {

        return(
          <RowComponent key={`dashboard-row-${index}`} cells={cells} isMaximized={false}
            responseData={cellData} loading={loading} parentSize={{ width: parentSize.width, height }}
            dashboard={context.dashboard!} setDashboard={updateDashboard} setSelectedCell={setSelectedCell}
            schemas={schemas} onUpdatePalette={onUpdatePalette} marqueeMode={marqueeTime > 0}/>
        );
      })
      }

      { selectedCell && schemas && context.dashboard &&
      <RowComponent cells={[ selectedCell ]} isMaximized={true}
        responseData={cellData} loading={loading} parentSize={{ width: parentSize.width, height }}
        dashboard={context.dashboard} setDashboard={updateDashboard} setSelectedCell={setSelectedCell}
        schemas={schemas} onUpdatePalette={onUpdatePalette} marqueeMode={marqueeTime > 0}/>
      }
    </div>
  );

};

export default InterruptionDashboard;
